import './edit-report-screen.scss';

import { faCaretDown, faCaretRight, faSave } from '@fortawesome/free-solid-svg-icons';
import { BAlert } from 'bootstrap-vue';
import { noop, set, update } from 'lodash';
import { first, switchMap, tap } from 'rxjs/operators';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';

import {
    ConditionReport,
    ConditionReportDataStore,
    Expression,
    ExpressionDataStore,
    expressionSelectors,
    getMinimumDefectAlterations,
    Item,
    ItemDataStore,
    itemSelectors,
    Manifestation,
    ManifestationDataStore,
    manifestationSelectors,
    SearchResult,
    Wemi,
    Work,
    WorkDataStore,
} from '@skinsoft/entity';
import { ReportFeatured, toReportFeatured$ } from '@skinsoft/entity-ui';
import { ReactiveVue } from '@skinsoft/reactive-vue';
import { toSearchFeatured$ } from '@skinsoft/search-data-store/src/featured';
import { Form, HeaderSearchFeaturedCard, SearchFeatured, toDateTxt, toInputDate, toTimestamp } from '@skinsoft/ui';
import { CollapsibleSection } from '@skinsoft/ui/src/collapsible-section';


import { empty, of } from 'rxjs';
import { CLOSE_CLOSE_REPORT_SCREEN_TOPIC, CLOSE_DRAWING_EDITOR_TOPIC, CLOSE_PDF_OVERVIEW_TOPIC, CLOSE_SIGNATURE_MODULE_TOPIC, DELETE_CURRENT_REPORT_TOPIC, DISPLAY_ALERT_TOPIC, OPEN_CLOSE_REPORT_SCREEN_TOPIC, OPEN_DRAWING_EDITOR_TOPIC, OPEN_PDF_OVERVIEW_TOPIC, OPEN_SIGNATURE_MODULE_TOPIC, REPORT_EDITOR_CLOSE_ALL_TABS_TOPIC, REPORT_EDITOR_OPEN_ALL_TABS_TOPIC, SAVE_CURRENT_REPORT_TOPIC } from '../../../busTopics';
import { CloseReportScreen } from '../../component/close-report-screen';
import { SignatureModuleScreen } from '../../component/signature-module/signature-module-screen';
import { PDFOverviewScreen } from '../../pdf-overview-screen';
import { AlertContainer } from '../alert-container';
import { CollapsibleHeader } from '../collapsible-header';
import { Tree } from '../defect-tree/tree';
import { DrawingEditor } from '../drawing-editor-components/drawing-editor';
import { ContextFieldsArea } from '../fields-areas/context-fields-area';
import { IdentifierFieldsArea } from '../fields-areas/identifiers-fields-area';
import { GeneralObservationsArea } from '../general-observations-area';
import { MediasFieldsArea } from '../medias-fields-area';


@Component({
    components: {
        sAlertContainer: AlertContainer,
        sCloseReportScreen: CloseReportScreen,
        sCollapsibleHeader: CollapsibleHeader,
        sCollapsibleSection: CollapsibleSection,
        sContextFieldsArea: ContextFieldsArea,
        sDrawingEditor: DrawingEditor,
        sGeneralObservationsArea: GeneralObservationsArea,
        sHeaderSearchFeaturedCard: HeaderSearchFeaturedCard,
        sIdentifiersFieldsArea: IdentifierFieldsArea,
        sMediasFieldsArea: MediasFieldsArea,
        sPdfOverviewScreen: PDFOverviewScreen,
        sSignatureModuleScreen: SignatureModuleScreen,

        sTree: Tree
    },
    template: require('./edit-report-screen.html'),
})
export class EditReportScreen extends ReactiveVue {

    get initialValues() {
        return {
            conditionReport: {},
        };
    }
    private get form() {
        return this.$refs.reportForm.form;
    }

    public $refs: {
        errorAlert: BAlert;
        reportForm: Form;
    };

    // Entities ids
    public reportId: string = "";

    // Entities
    public item: Item = null as any;
    public manifestation: Manifestation = null as any;
    public expression: Expression;
    public work: Work = null as any;
    public reportFeatured: ReportFeatured = null as any;
    public searchFeatured: SearchFeatured = null as any;
    public conditionReport: ConditionReport = null as any;

    // Drawing editor
    public drawingEditorParameters: any = {};

    // Icons
    public caretDownIcon = faCaretDown;
    public caretRightIcon = faCaretRight;
    public saveIcon = faSave;

    public reportTitle: string = "";
    public showDrawingEditor: boolean = false;
    public showPdfOverview: boolean = false;
    public showSignatureModule: boolean = false;
    public showCloseReportScreen: boolean = false;

    public hasItem: boolean = true;

    // Data Stores
    private $itemDataStore: ItemDataStore;
    private $workDataStore: WorkDataStore;
    private $manifestationDataStore: ManifestationDataStore;
    private $conditionReportDataStore: ConditionReportDataStore;
    private $expressionDataStore: ExpressionDataStore;

    private fieldName: string = "conditionReport";

    private saveErrorMessage: string = "";
    private saveSuccessMessage: string = "";
    private deleteErrorMessage: string = "";
    private deleteSuccessMessage: string = "";

    public mounted() {
        this.form.registerField(this.fieldName, noop, {
            data: true,
        });

        // Initializing them here to avoid a VueI18N bug when calling $t in async functions
        this.saveErrorMessage = this.$t("editReportScreen.saveAlert.errorMessage") as string;
        this.saveSuccessMessage = this.$t("editReportScreen.saveAlert.successMessage") as string;
        this.deleteErrorMessage = this.$t("editReportScreen.deleteAlert.errorMessage") as string;
        this.deleteSuccessMessage = this.$t("editReportScreen.deleteAlert.successMessage") as string;
    }

    // TODO Should be called by superclass but it is not the case => investigate later
    public beforeDestroy() {
        super.beforeDestroy()
    }

    public onCollapseAllClick() {
        this.emitOnBus(REPORT_EDITOR_CLOSE_ALL_TABS_TOPIC);
    }

    public onDeployAllClick() {
        this.emitOnBus(REPORT_EDITOR_OPEN_ALL_TABS_TOPIC)
    }

    public async saveReport() {
        const submitPromise = this.form.submit()
        if (submitPromise) {
            const alertPayload = {
                message: this.saveErrorMessage,
                variant: "danger"
            };
            submitPromise
                .then((response: any) => {
                    if (!(response instanceof Error)) {
                        alertPayload.message = this.saveSuccessMessage;
                        alertPayload.variant = "success"
                    }
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                })
        }

    }

    public async onSubmit(values: Record<any, any>) {
        return await this.$conditionReportDataStore.update(
            this.conditionReport.id,
            values.conditionReport)
            .pipe(
                first()
            )
            .toPromise()
    }

    @Watch('conditionReport', { deep: true })
    public onConditionReportChange(conditionReport) {

        // the mutator setFieldData is not working so we hack
        set(this.form.getState().values, 'conditionReport', conditionReport)

        // Converting report date to suitable format for form input
        update(this.form.getState().values, 'conditionReport.conditionReportCS.reportDate.startInterval.date',
            date => toInputDate(date));

        this.form.mutators.setFieldTouched('conditionReport');
    }

    public getDefects() {
        if (!this.conditionReport.conditionReportCS.alterationDetails) {
            this.conditionReport.conditionReportCS.alterationDetails = getMinimumDefectAlterations(this.$i18n.locale)
        }
        return this.conditionReport.conditionReportCS.alterationDetails;
    }

    public get signers() {
        if (!this.conditionReport.conditionReportCS.signers) {
            this.conditionReport.conditionReportCS.signers = []
        }
        return this.conditionReport.conditionReportCS.signers;
    }

    private deleteCurrentReport() {
        const deletePromise = this.$conditionReportDataStore.delete(this.conditionReport.id).pipe(first()).toPromise()
        if (deletePromise) {
            deletePromise
                .then((response: any) => {
                    const alertPayload = {
                        message: this.deleteSuccessMessage,
                        variant: "success"
                    };
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                })
                .catch(e => {

                    const alertPayload = {
                        message: this.deleteErrorMessage,
                        variant: "danger"
                    };
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                })
        }
    }

    private subscribe() {
        this.reportId = this.$route.params.reportId;

        // Retrieving data for collapsible header
        this.subscribeTo(this.$conditionReportDataStore.fetchOne(this.reportId)
            .pipe(
                // saving condition report object
                tap(conditionReport => this.conditionReport = conditionReport),
                switchMap((report) => toReportFeatured$(report, this.$i18n.locale)),
                // Retrieving one by one M, E, W to rebuild a searchFeatured
                switchMap(
                    (reportFeatured) => {
                        this.reportFeatured = reportFeatured
                        if (this.reportFeatured.involvedItem) {
                            return this.$itemDataStore.fetchOne(this.reportFeatured.involvedItem)
                        }
                        else {
                            // If no item is attached to report, stopping the rquest chain here
                            this.hasItem = false;
                            return empty()
                        }
                    }
                ),
                switchMap(
                    (item) => {
                        this.item = item;
                        return this.$manifestationDataStore.fetchOne(itemSelectors.manifestation(item))
                    }
                ),
                switchMap(
                    (manifestation) => {
                        this.manifestation = manifestation;
                        return this.$expressionDataStore.fetchOne(manifestationSelectors.expression(manifestation))
                    }
                ),
                switchMap(
                    (expression) => {
                        this.expression = expression;
                        const workId = expressionSelectors.work(expression);
                        if (workId) {
                            return this.$workDataStore.fetchOne(workId)
                        } else {
                            return of(null)
                        }

                    }
                ),
                switchMap(
                    (work) => {
                        // Combining I, M, W to rebuild a searchFeatured used in the header
                        if (work) {
                            this.work = work;
                        }
                        return toSearchFeatured$(
                            {
                                // id: '',
                                expression: this.expression,
                                item: this.item,
                                manifestation: this.manifestation,
                                work: this.work,
                            } as Wemi,
                            this.$i18n.locale,
                            false
                        )
                    }
                )
            ),
            (searchFeatured) => {
                this.searchFeatured = searchFeatured;
            },
            "search-featured-retrieval-chain"
        )

        // Subscribe to save event
        this.subscribeToBus(SAVE_CURRENT_REPORT_TOPIC, () => {
            this.saveReport()
        },
            'save-report-subscription')

        this.subscribeToBus(DELETE_CURRENT_REPORT_TOPIC, () => {
            this.deleteCurrentReport()
        },
            'delete-report-subscription')

        // Subscribe to annotation events to open drawing editor when needed
        this.subscribeToBus(OPEN_DRAWING_EDITOR_TOPIC, (message) => {
            this.showDrawingEditor = true
            this.showPdfOverview = false;

            if (message.payload) {
                this.drawingEditorParameters.annotation = message.payload.annotation
                this.drawingEditorParameters.annotationFieldName =
                    `${message.payload.annotationListFieldName}[${message.payload.annotationListFieldIndex}`
                this.drawingEditorParameters.annotationListFieldIndex = message.payload.annotationListFieldIndex;
                this.drawingEditorParameters.mediaId = message.payload.mediaId;
                this.drawingEditorParameters.mediaUrl = message.payload.mediaUrl;
                this.drawingEditorParameters.breadcrumbObject = message.payload.breadcrumbObject;
            }
        })

        this.subscribeToBus(CLOSE_DRAWING_EDITOR_TOPIC, async (message) => {
            if (message.payload) {
                if (message.payload.saveReport) {
                    await this.saveReport()
                }
            }
            this.showDrawingEditor = false
        })

        // Events to open/close pdf overview screen

        this.subscribeToBus(OPEN_PDF_OVERVIEW_TOPIC, (message) => {
            this.showDrawingEditor = false
            this.showCloseReportScreen = false;
            this.showSignatureModule = false
            this.showPdfOverview = true;
        })

        this.subscribeToBus(CLOSE_PDF_OVERVIEW_TOPIC, (message) => {
            this.showPdfOverview = false;
        })

        this.subscribeToBus(OPEN_SIGNATURE_MODULE_TOPIC, (message) => {
            this.showDrawingEditor = false
            this.showPdfOverview = false;
            this.showCloseReportScreen = false;
            this.showSignatureModule = true;
        })

        this.subscribeToBus(CLOSE_SIGNATURE_MODULE_TOPIC, (message) => {
            this.showSignatureModule = false;

        })

        this.subscribeToBus(OPEN_CLOSE_REPORT_SCREEN_TOPIC, (message) => {
            this.showDrawingEditor = false
            this.showCloseReportScreen = true;
            this.showSignatureModule = false;
        })

        this.subscribeToBus(CLOSE_CLOSE_REPORT_SCREEN_TOPIC, (message) => {
            this.showCloseReportScreen = false;
        })

    }

    public get associatedMediasFieldName() {
        return `${this.fieldName}.associatedMedias`
    }

    private unsubscribe() {
        this.unsubscribeFrom('search-featured-retrieval-chain');
        this.unsubscribeFrom('save-report-subscription');
        this.unsubscribeFrom('delete-report-subscription');
    }

    @Watch('reportFeatured')
    private buildTitle() {
        if (this.reportFeatured) {
            this.reportTitle = this.$t('editReportScreen.reportTitlePrefix', {
                refNumber: this.reportFeatured.referenceNumber,
            }) as string;
        }
    }

    @Watch('$route', { immediate: true })
    private onRouteChange() {
        this.unsubscribe()
        this.subscribe()
    }
}
